import { create } from 'zustand';

import * as authApi from '@/api/auth';
import { configureAuthClient } from '@/api/client';
import type { AuthErrorPayload, AuthUser, UserRole } from '@/api/auth.types';
import { clearRefreshToken, loadRefreshToken, saveRefreshToken } from '@/lib/secureStore';
import { mapAuthError } from '@/lib/authErrors';

/** Rôles autorisés sur la console admin (espace « staff » Harmaury). */
const ALLOWED_ROLES: ReadonlySet<UserRole> = new Set([
  'ADMIN',
  'RH',
  'CONSEILLER',
  'AGENT_TERRAIN',
]);

/**
 * Store d'authentification Harmaury (Phase 1).
 *
 * Règles :
 * - accessToken : *mémoire uniquement* (jamais persisté).
 * - refreshToken : IndexedDB chiffré AES-GCM via `secureStore`.
 * - user : mémoire ; re-hydraté via `/auth/me` au mount si refresh token présent.
 *
 * Phase : 'idle' (init), 'hydrating' (init → me|refresh), 'authenticated',
 * 'anonymous' (pas connecté, prêt). Permet de bloquer le rendu protégé tant
 * qu'on n'a pas tenté la ré-hydratation.
 */

export type AuthPhase = 'idle' | 'hydrating' | 'authenticated' | 'anonymous';

interface AuthState {
  phase: AuthPhase;
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  lastError: AuthErrorPayload | null;
  hydrate: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<string | null>;
  clearError: () => void;
}

let hydratePromise: Promise<void> | null = null;
let refreshPromise: Promise<string | null> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  phase: 'idle',
  user: null,
  accessToken: null,
  refreshToken: null,
  lastError: null,

  clearError: () => set({ lastError: null }),

  hydrate: () => {
    if (hydratePromise !== null) {
      return hydratePromise;
    }
    hydratePromise = (async () => {
      set({ phase: 'hydrating' });
      const stored = await loadRefreshToken();
      if (stored === null) {
        set({ phase: 'anonymous' });
        return;
      }
      set({ refreshToken: stored });
      // Avec uniquement un refresh, on tente direct un refresh pour obtenir un access.
      const newAccess = await get().refresh();
      if (newAccess === null) {
        set({ phase: 'anonymous', refreshToken: null, user: null });
        await clearRefreshToken();
        return;
      }
      try {
        // `/auth/me` renvoie l'utilisateur directement (pas `{ user }`).
        const me = await authApi.me();
        // Garde anti-boucle : ne JAMAIS rester 'authenticated' sans user valide,
        // sinon ProtectedRoute (→/login) et LoginPage (→/dashboard) bouclent en
        // redirection (replaceState infini → page figée/blanche).
        if (me !== null && me !== undefined && me.id !== undefined) {
          set({ phase: 'authenticated', user: me });
        } else {
          await clearRefreshToken();
          set({ phase: 'anonymous', user: null, accessToken: null, refreshToken: null });
        }
      } catch {
        await clearRefreshToken();
        set({ phase: 'anonymous', user: null, accessToken: null, refreshToken: null });
      }
    })().finally(() => {
      hydratePromise = null;
    });
    return hydratePromise;
  },

  login: async (email, password) => {
    set({ lastError: null });
    try {
      const resp = await authApi.login(email, password);

      // Cloisonnement côté client : le backend bloque déjà les rôles étrangers
      // (403), mais on vérifie aussi ici pour les cas où l'audience passerait
      // (ex. token JWT mal signé, bug réseau intercepté, ...).
      if (!ALLOWED_ROLES.has(resp.user.role)) {
        const payload: AuthErrorPayload = {
          kind: 'role_mismatch',
          message: 'Cet espace est réservé au personnel Harmaury.',
        };
        set({ lastError: payload });
        throw payload;
      }

      await saveRefreshToken(resp.refresh);
      set({
        phase: 'authenticated',
        user: resp.user,
        accessToken: resp.access,
        refreshToken: resp.refresh,
        lastError: null,
      });
    } catch (err) {
      // Si l'erreur est déjà un AuthErrorPayload (role_mismatch ci-dessus),
      // on ne la re-mappe pas.
      if (
        err !== null &&
        typeof err === 'object' &&
        'kind' in err &&
        'message' in err
      ) {
        throw err as AuthErrorPayload;
      }
      const payload = mapAuthError(err);
      set({ lastError: payload });
      throw payload;
    }
  },

  logout: async () => {
    const rt = get().refreshToken;
    if (rt !== null) {
      try {
        await authApi.logout(rt);
      } catch {
        // best-effort : on continue le logout local même si l'API rejette.
      }
    }
    await clearRefreshToken();
    set({
      phase: 'anonymous',
      user: null,
      accessToken: null,
      refreshToken: null,
      lastError: null,
    });
  },

  refresh: () => {
    if (refreshPromise !== null) {
      return refreshPromise;
    }
    refreshPromise = (async () => {
      const rt = get().refreshToken ?? (await loadRefreshToken());
      if (rt === null) {
        return null;
      }
      try {
        const resp = await authApi.refresh(rt);
        await saveRefreshToken(resp.refresh);
        set({ accessToken: resp.access, refreshToken: resp.refresh });
        return resp.access;
      } catch {
        return null;
      }
    })().finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  },
}));

/**
 * Câble le store sur l'axios client (à appeler une fois au bootstrap).
 */
export function wireAuthClient(): void {
  configureAuthClient({
    getAccessToken: () => useAuthStore.getState().accessToken,
    refresh: () => useAuthStore.getState().refresh(),
    onUnauthorized: () => {
      void useAuthStore.getState().logout();
    },
  });
}
